const GERMAN_CHAR_PATTERN = /[\u00C4\u00E4\u00D6\u00F6\u00DC\u00FC\u00DF\u1E9E]/g;
const GERMAN_CHAR_MAP = {
    '\u00C4': 'ae',
    '\u00E4': 'ae',
    '\u00D6': 'oe',
    '\u00F6': 'oe',
    '\u00DC': 'ue',
    '\u00FC': 'ue',
    '\u00DF': 'ss'
};
/**
 * Normalize string segments to slug friendly ASCII fragments.
 */
export function slugifySegment(input) {
    if (input === null || input === undefined) {
        return '';
    }
    let value = String(input).trim().toLowerCase();
    if (!value) {
        return '';
    }
    value = value.replace(/ß/g, 'ss');
    value = value.replace(GERMAN_CHAR_PATTERN, (match) => GERMAN_CHAR_MAP[match] || '');
    value = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    value = value.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return value.replace(/-+/g, '-');
}
/**
 * Build canonical product slug using brand, name, and weight information.
 */
export function generateProductSlug(product, options = {}) {
    const includeBrand = options.includeBrand !== false;
    const includeWeight = options.includeWeight !== false;
    const brandSegment = includeBrand ? slugifySegment(product.brand) : '';
    const nameSegment = slugifySegment(product.name);
    const weightSegment = includeWeight ? slugifySegment(product.weight) : '';
    const parts = [brandSegment, nameSegment, weightSegment].filter(Boolean);
    let slug = parts.join('-');
    if (!slug) {
        const fallbackSlug = slugifySegment(product.slug);
        if (fallbackSlug) {
            slug = fallbackSlug;
        }
    }
    if (!slug) {
        const idValue = product._id ?? product.id;
        const idSlug = slugifySegment(idValue);
        if (idSlug) {
            slug = idSlug;
        }
    }
    return slug;
}
/**
 * Enumerate slug variations for a product to support flexible matching.
 */
export function getProductSlugCandidates(product) {
    const candidates = new Set();
    const canonical = generateProductSlug(product);
    if (canonical) {
        candidates.add(canonical);
    }
    const brandName = generateProductSlug(product, { includeBrand: true, includeWeight: false });
    if (brandName) {
        candidates.add(brandName);
    }
    const nameWeight = generateProductSlug(product, { includeBrand: false, includeWeight: true });
    if (nameWeight) {
        candidates.add(nameWeight);
    }
    const nameOnly = generateProductSlug(product, { includeBrand: false, includeWeight: false });
    if (nameOnly) {
        candidates.add(nameOnly);
    }
    const explicitSlug = slugifySegment(product.slug);
    if (explicitSlug) {
        candidates.add(explicitSlug);
    }
    const idValue = product._id ?? product.id;
    const idSlug = slugifySegment(idValue);
    if (idSlug) {
        candidates.add(idSlug);
    }
    return Array.from(candidates);
}
/**
 * Derive category slug from product metadata if available.
 */
export function getCategorySlugFromProduct(product) {
    const directSlug = slugifySegment(product.categorySlug);
    if (directSlug) {
        return directSlug;
    }
    if (product.category && typeof product.category === 'object' && product.category !== null) {
        const categoryRecord = product.category;
        const objectSlug = slugifySegment(categoryRecord.slug);
        if (objectSlug) {
            return objectSlug;
        }
        const objectName = slugifySegment(categoryRecord.name);
        if (objectName) {
            return objectName;
        }
    }
    if (typeof product.category === 'string') {
        const categoryString = slugifySegment(product.category);
        if (categoryString) {
            return categoryString;
        }
    }
    const fallbackName = slugifySegment(product.categoryName);
    return fallbackName || null;
}
